import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Alert,
    Chip,
    Divider,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    Search as SearchIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    AccountBalance as AccountBalanceIcon,
    Receipt as ReceiptIcon,
    Download as DownloadIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { createUseStyles } from 'react-jss';
import axios from '../../axios';
import { jsPDF } from 'jspdf';
import Spinner from 'components/Spinner';

const useStyles = createUseStyles({
    pageContainer: {
        // minHeight: '100vh',
        // backgroundColor: '#f8fafc',
        // padding: '24px'
    },
    headerSection: {
        background: 'linear-gradient(135deg, #085d40 0%, #047857 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '32px',
        marginTop: '-24px',
        color: '#ffffff',
        boxShadow: '0 10px 30px rgba(8, 93, 64, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background:
                'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
        }
    },
    searchSection: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    statementCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
    },
    accountSummary: {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #bbf7d0'
    },
    transactionTable: {
        '& .MuiTableHead-root': {
            backgroundColor: '#085d40'
        },
        '& .MuiTableCell-head': {
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '12px',
            textTransform: 'uppercase',
            padding: '12px 16px'
        },
        '& .MuiTableCell-body': {
            padding: '12px 16px',
            fontSize: '13px',
            fontWeight: 500,
            color: '#374151',
            borderBottom: '1px solid #f3f4f6'
        },
        '& .MuiTableRow-root:nth-child(even)': {
            backgroundColor: '#f9fafb'
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: '#f3f4f6'
        }
    }
});

const IndividualSavingStatement = () => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState('');
    const [statement, setStatement] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleInputChange = (e) => {
        setSelectedContact(e.target.value);
        if (error) setError(''); // Clear error when user starts typing
    };

    const handleRetrieveStatement = async () => {
        if (!selectedContact.trim()) {
            setError('Please enter a valid contact number.');
            return;
        }

        setError('');
        setSuccess('');
        setIsLoading(true);

        const standardisedContact = '0' + selectedContact.slice(-9);

        try {
            const { data: userData } = await axios.get(`/farmer/users/${standardisedContact}`);
            const { data: statementData } = await axios.post(
                `/transactions/farmer/${standardisedContact}`
            );

            // Calculate total savings from transactions
            const totalSavings = statementData.data.reduce(
                (total, transaction) => total + transaction.amount,
                0
            );

            setStatement({
                farmer: userData.data,
                transactions: statementData.data,
                totalSavings
            });

            setSuccess(`Statement retrieved successfully for ${userData.data.name}`);
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            setError(
                'Statement with this contact does not exist. Please check the provided contact number or try again later.'
            );
            setStatement(null);
        }
    };

    const handleDownloadPDF = () => {
        if (!statement) return;

        const doc = new jsPDF();

        // Header
        doc.setFontSize(20);
        doc.setTextColor(8, 93, 64);
        doc.text('INDIVIDUAL SAVINGS STATEMENT', 20, 20);

        // Divider line
        doc.setDrawColor(8, 93, 64);
        doc.setLineWidth(0.5);
        doc.line(20, 25, 190, 25);

        // Account holder information
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text('ACCOUNT HOLDER INFORMATION', 20, 40);

        doc.setFontSize(12);
        doc.text(`Name: ${statement.farmer.name}`, 20, 50);
        doc.text(`Contact: ${statement.farmer.contact}`, 20, 60);
        doc.text(`Location: ${statement.farmer.location || 'N/A'}`, 20, 70);
        doc.text(`Total Savings: UGX ${statement.totalSavings.toLocaleString('en-US')}`, 20, 80);
        doc.text(`Statement Date: ${new Date().toLocaleDateString()}`, 20, 90);

        // Transactions section
        doc.setFontSize(14);
        doc.text('TRANSACTION HISTORY', 20, 110);

        let yOffset = 120;
        doc.setFontSize(10);

        // Table headers
        doc.setFont('helvetica', 'bold');
        doc.text('Date', 20, yOffset);
        doc.text('Payee', 60, yOffset);
        doc.text('Contact', 100, yOffset);
        doc.text('Amount (UGX)', 140, yOffset);

        yOffset += 10;
        doc.setFont('helvetica', 'normal');

        statement.transactions.forEach((transaction) => {
            if (yOffset > 270) {
                doc.addPage();
                yOffset = 20;
            }

            const transactionDate = new Date(transaction.createdAt).toLocaleDateString();
            doc.text(transactionDate, 20, yOffset);
            doc.text(transaction.payee.name, 60, yOffset);
            doc.text(transaction.payee.contact, 100, yOffset);
            doc.text(transaction.amount.toLocaleString('en-US'), 140, yOffset);
            yOffset += 10;
        });

        doc.save(`${statement.farmer.name}_Savings_Statement.pdf`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'UGX',
            currencyDisplay: 'code'
        })
            .format(amount)
            .replace('UGX', 'UGX ');
    };

    return (
        <div className={classes.pageContainer}>
            {/* Modern Header Section */}
            <div className={classes.headerSection}>
                <Grid container alignItems='center' spacing={3}>
                    <Grid item>
                        <ReceiptIcon sx={{ fontSize: 48, opacity: 0.9 }} />
                    </Grid>
                    <Grid item xs>
                        <Typography
                            variant='h4'
                            sx={{
                                fontWeight: 700,
                                marginBottom: '8px',
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            Individual Savings Statement
                        </Typography>
                        <Typography
                            variant='body1'
                            sx={{
                                opacity: 0.9,
                                fontSize: '16px',
                                fontWeight: 400
                            }}
                        >
                            Generate detailed savings statements for individual farmers
                        </Typography>
                    </Grid>
                </Grid>
            </div>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                    {/* Search Section */}
                    <Card className={classes.searchSection}>
                        <Typography
                            variant='h6'
                            sx={{
                                color: '#1f2937',
                                fontWeight: 600,
                                marginBottom: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <SearchIcon color='primary' />
                            Search Farmer
                        </Typography>

                        <TextField
                            fullWidth
                            label='Contact Number'
                            variant='outlined'
                            value={selectedContact}
                            onChange={handleInputChange}
                            placeholder="Enter farmer's contact number"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <PhoneIcon color='action' />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                marginBottom: '20px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px'
                                }
                            }}
                        />

                        <Button
                            variant='contained'
                            fullWidth
                            onClick={handleRetrieveStatement}
                            disabled={isLoading || !selectedContact.trim()}
                            sx={{
                                backgroundColor: '#059669',
                                color: '#ffffff',
                                padding: '12px',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(5, 150, 105, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#047857',
                                    boxShadow: '0 6px 20px rgba(5, 150, 105, 0.4)'
                                },
                                '&:disabled': {
                                    backgroundColor: '#9ca3af',
                                    color: '#ffffff'
                                }
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size='small' />
                                    <span style={{ marginLeft: 8 }}>Retrieving...</span>
                                </>
                            ) : (
                                <>
                                    <SearchIcon sx={{ marginRight: '8px' }} />
                                    Retrieve Statement
                                </>
                            )}
                        </Button>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={8}>
                    {/* Alerts */}
                    {error && (
                        <Alert
                            severity='error'
                            sx={{
                                marginBottom: '20px',
                                borderRadius: '12px',
                                '& .MuiAlert-icon': {
                                    fontSize: '24px'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert
                            severity='success'
                            sx={{
                                marginBottom: '20px',
                                borderRadius: '12px',
                                '& .MuiAlert-icon': {
                                    fontSize: '24px'
                                }
                            }}
                        >
                            {success}
                        </Alert>
                    )}

                    {/* Statement Display */}
                    {statement && (
                        <Card className={classes.statementCard}>
                            {/* Account Summary */}
                            <CardContent sx={{ padding: '32px' }}>
                                <div className={classes.accountSummary}>
                                    <Grid container spacing={3} alignItems='center'>
                                        <Grid item>
                                            <AccountBalanceIcon
                                                sx={{
                                                    fontSize: 48,
                                                    color: '#059669',
                                                    opacity: 0.8
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography
                                                variant='h5'
                                                sx={{
                                                    color: '#065f46',
                                                    fontWeight: 700,
                                                    marginBottom: '8px'
                                                }}
                                            >
                                                Account Summary
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <PersonIcon
                                                            sx={{ fontSize: 18, color: '#059669' }}
                                                        />
                                                        <Typography
                                                            variant='body2'
                                                            color='textSecondary'
                                                        >
                                                            Account Holder
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant='h6'
                                                        sx={{ fontWeight: 600, color: '#1f2937' }}
                                                    >
                                                        {statement.farmer.name}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <PhoneIcon
                                                            sx={{ fontSize: 18, color: '#059669' }}
                                                        />
                                                        <Typography
                                                            variant='body2'
                                                            color='textSecondary'
                                                        >
                                                            Contact
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant='h6'
                                                        sx={{ fontWeight: 600, color: '#1f2937' }}
                                                    >
                                                        {statement.farmer.contact}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <LocationIcon
                                                            sx={{ fontSize: 18, color: '#059669' }}
                                                        />
                                                        <Typography
                                                            variant='body2'
                                                            color='textSecondary'
                                                        >
                                                            Location
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant='h6'
                                                        sx={{ fontWeight: 600, color: '#1f2937' }}
                                                    >
                                                        {statement.farmer.location ||
                                                            'Not specified'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <TrendingUpIcon
                                                            sx={{ fontSize: 18, color: '#059669' }}
                                                        />
                                                        <Typography
                                                            variant='body2'
                                                            color='textSecondary'
                                                        >
                                                            Total Savings
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={formatCurrency(
                                                            statement.totalSavings
                                                        )}
                                                        sx={{
                                                            backgroundColor: '#059669',
                                                            color: '#ffffff',
                                                            fontSize: '16px',
                                                            fontWeight: 700,
                                                            padding: '8px 4px',
                                                            height: 'auto'
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>

                                <Divider sx={{ margin: '24px 0' }} />

                                {/* Transactions Table */}
                                <Typography
                                    variant='h6'
                                    sx={{
                                        color: '#1f2937',
                                        fontWeight: 600,
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <ReceiptIcon color='primary' />
                                    Transaction History ({statement.transactions.length}{' '}
                                    transactions)
                                </Typography>

                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                        maxHeight: 400,
                                        overflow: 'auto'
                                    }}
                                    className={classes.transactionTable}
                                >
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Payee</TableCell>
                                                <TableCell>Contact</TableCell>
                                                <TableCell align='right'>Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {statement.transactions.map((transaction, index) => (
                                                <TableRow key={transaction._id}>
                                                    <TableCell>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {new Date(
                                                                transaction.createdAt
                                                            ).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </Typography>
                                                        <Typography
                                                            variant='caption'
                                                            color='textSecondary'
                                                        >
                                                            {new Date(
                                                                transaction.createdAt
                                                            ).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1f2937'
                                                            }}
                                                        >
                                                            {transaction.payee.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{ fontFamily: 'monospace' }}
                                                        >
                                                            {transaction.payee.contact}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align='right'>
                                                        <Chip
                                                            label={`UGX ${transaction.amount.toLocaleString(
                                                                'en-US'
                                                            )}`}
                                                            size='small'
                                                            sx={{
                                                                backgroundColor: '#dcfce7',
                                                                color: '#059669',
                                                                fontWeight: 600,
                                                                border: '1px solid #bbf7d0'
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Download Button */}
                                <Box sx={{ marginTop: '24px', textAlign: 'center' }}>
                                    <Button
                                        variant='contained'
                                        onClick={handleDownloadPDF}
                                        sx={{
                                            backgroundColor: '#3b82f6',
                                            color: '#ffffff',
                                            padding: '12px 24px',
                                            borderRadius: '12px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                                            '&:hover': {
                                                backgroundColor: '#2563eb',
                                                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)'
                                            }
                                        }}
                                    >
                                        <DownloadIcon sx={{ marginRight: '8px' }} />
                                        Download PDF Statement
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default IndividualSavingStatement;
